import { useState, useEffect } from 'react';
import { useAuth } from '../../context/AuthContext';
import { db } from '../../firebase';
import { collection, query, where, getDocs, orderBy, limit, doc, getDoc } from 'firebase/firestore';
import { Link } from 'react-router-dom';
import {
  Wallet, BookOpen, Users, Plus, TrendingUp, Loader2,
  Sparkles, UserCircle, Clock, GraduationCap, ArrowLeft, Star
} from 'lucide-react';

export default function InstructorOverview() {
  const { currentUser, userData } = useAuth();
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState({ totalStudents: 0, totalRevenue: 0, activeCourses: 0 });
  const [latestCourses, setLatestCourses] = useState([]);
  const [latestEnrollments, setLatestEnrollments] = useState([]);
  const [latestActivities, setLatestActivities] = useState([]);

  useEffect(() => {
    const fetchAll = async () => {
      try {
        // 1. Fetch instructor's courses
        const coursesQ = query(
          collection(db, 'courses'),
          where('instructorId', '==', currentUser.uid)
        );
        const coursesSnap = await getDocs(coursesQ);
        const myCourses = coursesSnap.docs.map(d => ({ id: d.id, ...d.data() }));

        // Sort by createdAt descending for "latest courses"
        const sortedCourses = [...myCourses].sort((a, b) => {
          const aTime = a.createdAt?.toDate?.()?.getTime() || 0;
          const bTime = b.createdAt?.toDate?.()?.getTime() || 0;
          return bTime - aTime;
        });
        setLatestCourses(sortedCourses.slice(0, 3));

        // 2. Fetch all enrollments for these courses
        let allEnrollments = [];
        const commissionRate = (userData?.instructorData?.commissionRate || 0) / 100;
        let revenue = 0;

        for (const course of myCourses) {
          const curriculum = course.curriculum || [];
          const totalLessonsCount = curriculum.reduce((acc, unit) => acc + (unit.lessons?.length || 0), 0) || course.totalLessons || 0;

          const enrollmentsQ = query(
            collection(db, 'enrollments'),
            where('courseId', '==', course.id)
          );
          const enrollmentsSnap = await getDocs(enrollmentsQ);

          for (const enrollDoc of enrollmentsSnap.docs) {
            const enrollData = enrollDoc.data();
            const paidAmount = enrollData.actualPaidAmount || 0;
            revenue += paidAmount * commissionRate;

            allEnrollments.push({
              id: enrollDoc.id,
              ...enrollData,
              courseTitle: course.title,
              courseThumbnail: course.thumbnail,
              courseTotalLessons: totalLessonsCount,
            });
          }
        }

        // Sort enrollments by enrolledAt
        allEnrollments.sort((a, b) => {
          const aTime = a.enrolledAt?.toDate?.()?.getTime() || 0;
          const bTime = b.enrolledAt?.toDate?.()?.getTime() || 0;
          return bTime - aTime;
        });

        // 3. Resolve student names for latest 5 enrollments
        const latestEnr = allEnrollments.slice(0, 5);
        const resolvedEnrollments = await Promise.all(
          latestEnr.map(async (enr) => {
            try {
              const userSnap = await getDoc(doc(db, 'users', enr.userId));
              const uData = userSnap.exists() ? userSnap.data() : {};
              return {
                ...enr,
                studentName: uData.name || uData.nameAr || 'طالب',
                studentPhoto: uData.photoURL || '',
                enrollDate: enr.enrolledAt?.toDate?.()?.toLocaleDateString('ar-EG', {
                  month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit'
                }) || 'غير محدد',
              };
            } catch {
              return { ...enr, studentName: 'طالب', studentPhoto: '', enrollDate: 'غير محدد' };
            }
          })
        );
        setLatestEnrollments(resolvedEnrollments);

        // 4. Build "latest activities" from enrollments with completedLessons
        const activities = [];
        for (const enr of allEnrollments) {
          if (enr.completedLessons?.length > 0) {
            try {
              const userSnap = await getDoc(doc(db, 'users', enr.userId));
              const uData = userSnap.exists() ? userSnap.data() : {};

              const completedCount = enr.completedLessons.length;
              const totalLess = enr.courseTotalLessons || 1; 
              const progressPct = Math.min(100, Math.round((completedCount / totalLess) * 100));

              activities.push({
                id: enr.id,
                studentName: uData.name || uData.nameAr || 'طالب',
                studentPhoto: uData.photoURL || '',
                courseTitle: enr.courseTitle,
                lessonsCompleted: completedCount,
                progress: progressPct,
                score: enr.pointsEarned || enr.score || 0,
              });
            } catch { /* skip */ }
          }
          if (activities.length >= 5) break;
        }
        setLatestActivities(activities);

        // 5. Set stats
        setStats({
          totalStudents: allEnrollments.length,
          totalRevenue: revenue,
          activeCourses: myCourses.length,
        });
      } catch (err) {
        console.error("Error fetching instructor overview:", err);
      } finally {
        setLoading(false);
      }
    };

    if (currentUser) fetchAll();
  }, [currentUser, userData]);

  if (loading) {
    return (
      <div className="flex items-center justify-center p-20">
        <Loader2 className="w-8 h-8 animate-spin text-amber-500" />
      </div>
    );
  }

  const canUpload = userData?.instructorData?.canUploadCourses;
  const greetingTime = new Date().getHours();
  const greeting = greetingTime < 12 ? 'صباح الخير' : greetingTime < 17 ? 'مساء الخير' : 'مساء النور';

  return (
    <div className="space-y-8 animate-in fade-in slide-in-from-bottom-4 duration-700">

      {/* ── Creative Welcome Banner ────────────────────────────── */}
      <div className="relative bg-gradient-to-bl from-slate-900 via-slate-800 to-amber-900/80 dark:from-[#0a0a0a] dark:via-zinc-900 dark:to-amber-950/60 rounded-3xl p-8 md:p-10 overflow-hidden shadow-2xl group border border-white/5">
        {/* Ambient Glows */}
        <div className="absolute top-0 left-0 w-72 h-72 bg-amber-500/20 rounded-full blur-[100px] -translate-x-1/3 -translate-y-1/3 group-hover:scale-125 transition-transform duration-1000 pointer-events-none"></div>
        <div className="absolute bottom-0 right-0 w-56 h-56 bg-amber-600/15 rounded-full blur-[80px] translate-x-1/3 translate-y-1/3 pointer-events-none"></div>
        <div className="absolute top-4 right-6 opacity-10 text-[10rem] font-black text-white pointer-events-none select-none leading-none">لُباب</div>

        <div className="relative z-10 flex flex-col md:flex-row items-start md:items-center justify-between gap-6">
          <div className="flex-1">
            <div className="flex items-center gap-3 mb-3">
              <Sparkles className="w-6 h-6 text-amber-400 animate-pulse" />
              <span className="text-amber-400/80 font-bold text-sm tracking-wider">{greeting} 👋</span>
            </div>
            <h1 className="text-3xl md:text-4xl font-black text-white mb-3 leading-tight">
              أهلاً بعودتك، <span className="text-transparent bg-clip-text bg-gradient-to-l from-amber-300 to-amber-500">{userData?.name}</span>!
            </h1>
            <p className="text-slate-300/80 font-medium text-lg max-w-xl">
              استوديو التدريس الخاص بك جاهز. تابع طلابك، راجع تقدمهم، وألهم الجيل القادم من المبدعين.
            </p>
          </div>

          {canUpload ? (
            <button className="bg-amber-500 hover:bg-amber-400 text-slate-900 px-6 py-3.5 rounded-xl font-black flex items-center gap-2 shadow-lg shadow-amber-500/30 transition-all hover:-translate-y-1 hover:shadow-xl shrink-0">
              <Plus className="w-5 h-5" />
              إضافة كورس جديد
            </button>
          ) : (
            <div className="px-5 py-3 bg-white/10 backdrop-blur-md text-white/60 font-bold rounded-xl border border-white/10 text-sm shrink-0">
              صلاحية الرفع معطلة
            </div>
          )}
        </div>
      </div>

      {/* ── Analytics Widgets ──────────────────────────────────── */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        {/* Revenue */}
        <div className="bg-gradient-to-br from-slate-900 to-slate-800 dark:from-zinc-900 dark:to-black rounded-3xl p-6 shadow-2xl relative overflow-hidden group border border-slate-800 dark:border-white/10">
          <div className="absolute top-0 right-0 w-32 h-32 bg-amber-500/20 blur-2xl rounded-full translate-x-10 -translate-y-10 group-hover:scale-150 transition-transform duration-700"></div>
          <div className="relative z-10">
            <div className="flex items-center gap-4 mb-4">
              <div className="w-12 h-12 bg-white/10 rounded-2xl flex items-center justify-center backdrop-blur-md">
                <Wallet className="w-6 h-6 text-amber-400" />
              </div>
              <h3 className="text-slate-300 font-bold text-sm">إجمالي أرباحك ({userData?.instructorData?.commissionRate || 0}%)</h3>
            </div>
            <div className="text-4xl font-black text-white px-1 font-mono">${stats.totalRevenue.toFixed(2)}</div>
            <div className="mt-4 flex items-center gap-2 text-emerald-400 text-xs font-bold bg-white/5 w-max px-3 py-1 rounded-full">
              <TrendingUp className="w-3.5 h-3.5" /> بناءً على المبالغ المحصّلة
            </div>
          </div>
        </div>

        {/* Students */}
        <div className="bg-white dark:bg-zinc-900 rounded-3xl p-6 shadow-xl shadow-slate-200/50 dark:shadow-none border border-slate-200 dark:border-white/10 relative overflow-hidden group">
          <div className="absolute bottom-0 left-0 w-24 h-24 bg-blue-500/10 rounded-full blur-2xl group-hover:scale-150 transition-transform duration-700 pointer-events-none"></div>
          <div className="relative z-10">
            <div className="flex items-center gap-4 mb-4">
              <div className="w-12 h-12 bg-blue-50 dark:bg-blue-500/10 rounded-2xl flex items-center justify-center border border-blue-100 dark:border-blue-500/20">
                <Users className="w-6 h-6 text-blue-600 dark:text-blue-400" />
              </div>
              <h3 className="text-slate-500 dark:text-zinc-400 font-bold text-sm">إجمالي طلابك</h3>
            </div>
            <div className="text-4xl font-black text-slate-900 dark:text-white px-1">{stats.totalStudents}</div>
          </div>
        </div>

        {/* Courses */}
        <div className="bg-white dark:bg-zinc-900 rounded-3xl p-6 shadow-xl shadow-slate-200/50 dark:shadow-none border border-slate-200 dark:border-white/10 relative overflow-hidden group">
          <div className="absolute bottom-0 left-0 w-24 h-24 bg-indigo-500/10 rounded-full blur-2xl group-hover:scale-150 transition-transform duration-700 pointer-events-none"></div>
          <div className="relative z-10">
            <div className="flex items-center gap-4 mb-4">
              <div className="w-12 h-12 bg-indigo-50 dark:bg-indigo-500/10 rounded-2xl flex items-center justify-center border border-indigo-100 dark:border-indigo-500/20">
                <BookOpen className="w-6 h-6 text-indigo-600 dark:text-indigo-400" />
              </div>
              <h3 className="text-slate-500 dark:text-zinc-400 font-bold text-sm">دوراتك النشطة</h3>
            </div>
            <div className="text-4xl font-black text-slate-900 dark:text-white px-1">{stats.activeCourses}</div>
          </div>
        </div>
      </div>

      {/* ── Dynamic Data Sections ─────────────────────────────── */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">

        {/* Latest Courses */}
        <div className="bg-white dark:bg-zinc-900 rounded-3xl border border-slate-200 dark:border-white/10 overflow-hidden shadow-sm">
          <div className="p-6 pb-4 flex items-center justify-between border-b border-slate-100 dark:border-white/5">
            <h3 className="text-lg font-black text-slate-900 dark:text-white flex items-center gap-2">
              <BookOpen className="w-5 h-5 text-amber-500" /> أحدث دوراتك
            </h3>
            <Link to="/instructor-dashboard/courses" className="text-sm font-bold text-amber-600 dark:text-amber-400 hover:underline flex items-center gap-1">
              عرض الكل <ArrowLeft className="w-4 h-4" />
            </Link>
          </div>
          <div className="divide-y divide-slate-50 dark:divide-white/5">
            {latestCourses.length === 0 ? (
              <div className="p-10 text-center text-slate-400 dark:text-zinc-500 font-medium">
                لا توجد دورات حتى الآن
              </div>
            ) : (
              latestCourses.map(course => (
                <div key={course.id} className="flex items-center gap-4 p-4 hover:bg-slate-50/50 dark:hover:bg-white/[0.02] transition-colors">
                  <div className="w-14 h-14 rounded-xl overflow-hidden bg-slate-100 dark:bg-zinc-800 shrink-0 border border-slate-200 dark:border-white/10">
                    {course.thumbnail ? (
                      <img src={course.thumbnail} alt={course.title} className="w-full h-full object-cover" />
                    ) : (
                      <div className="w-full h-full flex items-center justify-center">
                        <BookOpen className="w-6 h-6 text-slate-300 dark:text-zinc-600" />
                      </div>
                    )}
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="font-bold text-slate-900 dark:text-white truncate">{course.title}</p>
                    <p className="text-xs text-slate-400 dark:text-zinc-500 font-medium mt-0.5">
                      {course.studentsCount || 0} طالب • ${course.price || 0}
                    </p>
                  </div>
                  <span className={`px-2.5 py-1 rounded-lg text-xs font-bold shrink-0 ${
                    course.status === 'published' 
                      ? 'bg-emerald-50 text-emerald-600 dark:bg-emerald-500/10 dark:text-emerald-400 border border-emerald-100 dark:border-emerald-500/20' 
                      : 'bg-slate-100 text-slate-500 dark:bg-white/5 dark:text-zinc-400 border border-slate-200 dark:border-white/10'
                  }`}>
                    {course.status === 'published' ? 'منشورة' : 'مسودة'}
                  </span>
                </div>
              ))
            )}
          </div>
        </div>

        {/* Latest Enrollments */}
        <div className="bg-white dark:bg-zinc-900 rounded-3xl border border-slate-200 dark:border-white/10 overflow-hidden shadow-sm">
          <div className="p-6 pb-4 flex items-center justify-between border-b border-slate-100 dark:border-white/5">
            <h3 className="text-lg font-black text-slate-900 dark:text-white flex items-center gap-2">
              <GraduationCap className="w-5 h-5 text-blue-500" /> أحدث التسجيلات
            </h3>
            <Link to="/instructor-dashboard/students" className="text-sm font-bold text-amber-600 dark:text-amber-400 hover:underline flex items-center gap-1">
              عرض الكل <ArrowLeft className="w-4 h-4" />
            </Link>
          </div>
          <div className="divide-y divide-slate-50 dark:divide-white/5">
            {latestEnrollments.length === 0 ? (
              <div className="p-10 text-center text-slate-400 dark:text-zinc-500 font-medium">
                لا توجد تسجيلات حتى الآن
              </div>
            ) : (
              latestEnrollments.map((enr, i) => (
                <div key={enr.id || i} className="flex items-center gap-4 p-4 hover:bg-slate-50/50 dark:hover:bg-white/[0.02] transition-colors">
                  <div className="w-10 h-10 rounded-full bg-slate-100 dark:bg-zinc-800 flex items-center justify-center overflow-hidden shrink-0 border border-slate-200 dark:border-white/10">
                    {enr.studentPhoto ? (
                      <img src={enr.studentPhoto} alt={enr.studentName} className="w-full h-full object-cover" />
                    ) : (
                      <UserCircle className="w-6 h-6 text-slate-400 dark:text-zinc-500" />
                    )}
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="font-bold text-slate-900 dark:text-white text-sm truncate">{enr.studentName}</p>
                    <p className="text-xs text-slate-400 dark:text-zinc-500 font-medium truncate">{enr.courseTitle}</p>
                  </div>
                  <div className="text-left shrink-0">
                    <span className="flex items-center gap-1 text-xs text-slate-400 dark:text-zinc-500 font-medium">
                      <Clock className="w-3.5 h-3.5" /> {enr.enrollDate}
                    </span>
                  </div>
                </div>
              ))
            )}
          </div>
        </div>
      </div>

      {/* Latest Student Activities */}
      {latestActivities.length > 0 && (
        <div className="bg-white dark:bg-zinc-900 rounded-3xl border border-slate-200 dark:border-white/10 overflow-hidden shadow-sm">
          <div className="p-6 pb-4 border-b border-slate-100 dark:border-white/5">
            <h3 className="text-lg font-black text-slate-900 dark:text-white flex items-center gap-2">
              <TrendingUp className="w-5 h-5 text-emerald-500" /> نشاط الطلاب الأخير
            </h3>
          </div>
          <div className="divide-y divide-slate-50 dark:divide-white/5">
            {latestActivities.map((act, i) => {
              const p = act.progress;
              let pColor = "bg-slate-50 dark:bg-zinc-800/80 text-slate-600 dark:text-zinc-400 border-slate-200 dark:border-white/10";
              if (p === 100) pColor = "bg-emerald-50 dark:bg-emerald-500/10 text-emerald-600 dark:text-emerald-400 border-emerald-100 dark:border-emerald-500/20";
              else if (p >= 75) pColor = "bg-blue-50 dark:bg-blue-500/10 text-blue-600 dark:text-blue-400 border-blue-100 dark:border-blue-500/20";
              else if (p >= 50) pColor = "bg-amber-50 dark:bg-amber-500/10 text-amber-600 dark:text-amber-400 border-amber-100 dark:border-amber-500/20";
              else if (p > 0) pColor = "bg-orange-50 dark:bg-orange-500/10 text-orange-600 dark:text-orange-400 border-orange-100 dark:border-orange-500/20";

              return (
                <div key={act.id || i} className="flex items-center gap-4 p-4 hover:bg-slate-50/50 dark:hover:bg-white/[0.02] transition-colors">
                  <div className="w-10 h-10 rounded-full bg-slate-100 dark:bg-zinc-800 flex items-center justify-center shrink-0">
                    {act.studentPhoto ? (
                      <img src={act.studentPhoto} alt={act.studentName} className="w-full h-full rounded-full object-cover" />
                    ) : (
                      <UserCircle className="w-6 h-6 text-slate-400 dark:text-zinc-500" />
                    )}
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-bold text-slate-900 dark:text-white">
                      <span className="text-amber-600 dark:text-amber-400">{act.studentName}</span>
                      {' '}أكمل{' '}
                      <span className="font-black">{act.lessonsCompleted}</span>
                      {' '}درس في{' '}
                      <span className="text-slate-600 dark:text-zinc-300">{act.courseTitle}</span>
                    </p>
                  </div>
                  <div className="shrink-0 flex items-center gap-2">
                    <span className="inline-flex items-center gap-1.5 px-2.5 py-1 bg-slate-50 dark:bg-white/5 text-slate-600 dark:text-zinc-300 rounded-full font-bold text-xs border border-slate-200 dark:border-white/10" title="الدرجات">
                      <Star className="w-3.5 h-3.5 text-amber-500" /> {act.score}
                    </span>
                    <span className={`inline-flex items-center gap-1 px-2.5 py-1 rounded-full font-bold text-xs border ${pColor}`}>
                      <TrendingUp className="w-3.5 h-3.5 shrink-0" /> {act.progress}%
                    </span>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}
